import { Browser, Code } from '@phosphor-icons/react';
import { EventSource } from 'eventsource';
import { getAuth } from 'firebase/auth';
import clsx from 'clsx';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { EquivalentCode } from 'src/components/EquivalentCode';
import { Tab } from 'src/components/Tab';
import { WidgetConfig } from 'src/components/WidgetConfig';
import { Agent } from 'src/interfaces/agent.interface';
import { Call } from 'src/interfaces/call.interface';
import { Prompt } from 'src/interfaces/prompt.interface';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { AdvancedSettings } from './components/AdvancedSettings';
import { AgentCaller } from './components/AgentCaller';
import { BasicInformation } from './components/BasicInformation';
import { PostCall } from './components/PostCall';
import { useCallback, useEffect, useRef, useState } from 'react';

const PROMPT_STREAM_ENDPOINT = `${process.env.REACT_APP_BASE_API_URL}/prompt-builder/chat/stream`;

type TabsAgentProps = {
  agent: Agent;
  setAgent: (value: React.SetStateAction<Agent>) => void;
  setLoading: (loading: boolean) => void;
  loading: boolean;
  defaultPrompt: Prompt | undefined;
  finishAction: (() => Promise<void>) | undefined;
  setModalAgentNumbersOpen: (open: boolean) => void;
  calls?: Call[];
  activeTab: number;
  setActiveTab: (index: number) => void;
};

export const TabItem = ({
  children,
  agent,
  setModalAgentNumbersOpen,
  calls = [],
  contextObject,
  setContextObject,
}: {
  children: React.ReactNode;
  agent: Agent;
  setModalAgentNumbersOpen: (open: boolean) => void;
  calls?: Call[];
  contextObject: Record<string, string>;
  setContextObject: (context: Record<string, string>) => void;
}) => {
  return (
    <div
      className={clsx(
        'flex',
        'flex-col',
        'md:grid',
        'md:grid-cols-[calc(60%-0.5rem)_calc(40%-0.5rem)]',
        'lg:grid-cols-[calc(65%-0.5rem)_calc(35%-0.5rem)]',
        'gap-x-4',
        'justify-between',
      )}
    >
      <div className="p-6 flex flex-col h-full w-full">{children}</div>

      <AgentCaller
        agent={agent}
        setModalAgentNumbersOpen={setModalAgentNumbersOpen}
        calls={calls}
        contextObject={contextObject}
        setContextObject={setContextObject}
      />
    </div>
  );
};

export const TabsAgent = ({
  agent,
  setAgent,
  setLoading,
  loading,
  defaultPrompt,
  finishAction,
  setModalAgentNumbersOpen,
  calls = [],
  activeTab,
  setActiveTab,
}: TabsAgentProps) => {
  const endpoint = agent.id ? `agents/update?id=${agent.id}` : 'agents/create';

  const notification = useNotification();
  const { environment } = useEnvironment();
  const environmentId = environment?.envId;
  const eventSourceRef = useRef<EventSource | null>(null);

  const [promptGenerating, setPromptGenerating] = useState(false);
  const [promptStreaming, setPromptStreaming] = useState(false);
  const [token, setToken] = useState<string | undefined>();
  const [contextObject, setContextObject] = useState<Record<string, string>>(
    {},
  );

  useEffect(() => {
    let isMounted = true;
    async function fetchToken() {
      const fetchedToken = await getAuth().currentUser?.getIdToken();
      if (isMounted) {
        setToken(fetchedToken);
      }
    }

    fetchToken();

    return () => {
      isMounted = false;
      eventSourceRef.current?.close();
    };
  }, []);

  const updatePromptContent = useCallback(
    (content: string) => {
      setAgent((prev) => ({
        ...prev,
        prompt: {
          ...(defaultPrompt ?? {}),
          content,
        },
      }));
    },
    [setAgent, defaultPrompt],
  );

  const handleGeneratePrompt = useCallback(
    async (input: string) => {
      if (!input.trim()) {
        notification.error(
          'Please describe the agent before generating a prompt.',
        );
        return;
      }

      eventSourceRef.current?.close();

      const auth = getAuth();
      const authToken = token ?? (await auth.currentUser?.getIdToken());

      if (!authToken) {
        notification.error('Unable to authenticate prompt generation.');
        setPromptGenerating(false);
        setPromptStreaming(false);
        return;
      }

      if (!token) {
        setToken(authToken);
      }

      setPromptGenerating(true);
      setPromptStreaming(false);

      let completeMessage = '';

      const query = new URLSearchParams({
        last_message: input,
      });

      const eventSource = new EventSource(
        `${PROMPT_STREAM_ENDPOINT}?${query.toString()}`,
        {
          fetch: (resource, init) =>
            fetch(resource, {
              ...init,
              headers: {
                ...init?.headers,
                Authorization: `Bearer ${authToken}`,
                'x-env': environmentId || '',
              },
            }),
        },
      );

      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        const response = JSON.parse(event.data);

        if (response.type === 'completed') {
          eventSource.close();
          eventSourceRef.current = null;
          setPromptStreaming(false);
          setPromptGenerating(false);
          return;
        }

        setPromptGenerating(false);
        setPromptStreaming(true);
        completeMessage += response.text;
        updatePromptContent(completeMessage);
      };

      eventSource.onerror = () => {
        notification.error(
          'Failed to generate prompt. Please wait a minute and try again.',
        );
        eventSource.close();
        eventSourceRef.current = null;
        setPromptStreaming(false);
        setPromptGenerating(false);
      };
    },
    [token, notification, environmentId, updatePromptContent],
  );

  return (
    <Tab
      className="pl-2"
      selectedIndex={activeTab}
      onChange={setActiveTab}
      items={[
        {
          label: 'Basic Information',
          content: (
            <TabItem
              agent={agent}
              setModalAgentNumbersOpen={setModalAgentNumbersOpen}
              calls={calls}
              contextObject={contextObject}
              setContextObject={setContextObject}
            >
              <BasicInformation
                agent={agent}
                setAgent={setAgent}
                loading={loading}
                defaultPrompt={defaultPrompt}
                promptGenerating={promptGenerating}
                promptStreaming={promptStreaming}
                onPromptContentChange={updatePromptContent}
                onGeneratePrompt={handleGeneratePrompt}
              />
            </TabItem>
          ),
        },
        {
          label: 'Advanced Settings',
          content: (
            <TabItem
              agent={agent}
              setModalAgentNumbersOpen={setModalAgentNumbersOpen}
              calls={calls}
              contextObject={contextObject}
              setContextObject={setContextObject}
            >
              <AdvancedSettings
                agent={agent}
                setAgent={setAgent}
                setLoading={setLoading}
                loading={loading}
                defaultPrompt={defaultPrompt}
                finishAction={finishAction}
              />
            </TabItem>
          ),
        },
        {
          label: 'Post-Call',
          content: (
            <TabItem
              agent={agent}
              setModalAgentNumbersOpen={setModalAgentNumbersOpen}
              calls={calls}
              contextObject={contextObject}
              setContextObject={setContextObject}
            >
              <PostCall agent={agent} setAgent={setAgent} loading={loading} />
            </TabItem>
          ),
        },
        {
          label: (
            <div className="flex">
              <Browser className="mr-2" size={20} />
              Web Widget
            </div>
          ),
          content: (
            <TabItem
              agent={agent}
              setModalAgentNumbersOpen={setModalAgentNumbersOpen}
              calls={calls}
              contextObject={contextObject}
              setContextObject={setContextObject}
            >
              <WidgetConfig agent={agent} />
            </TabItem>
          ),
        },
        {
          label: (
            <div className="flex">
              <Code className="mr-2" size={20} />
              Show equivalent code
            </div>
          ),
          content: (
            <ContentWrapper>
              <EquivalentCode
                payload={agent}
                endpoint={endpoint}
                method="POST"
              />
            </ContentWrapper>
          ),
        },
      ]}
      panelWrapperClassName="mt-0"
    />
  );
};
